import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import store from 'store';

const QUEUE_KEY = 'flc.android.tapDiagnostics.queue.v1';
const DEVICE_KEY = 'flcMobileWrapperDeviceId';
const MAX_WEB_EVENTS = 80;
const MAX_QUEUE_ITEMS = 8;
const CLICK_DELAY_MS = 500;
const NO_CLICK_MS = 1000;
const MAIN_THREAD_INTERVAL_MS = 250;
const MAIN_THREAD_STALL_MS = 500;
const NATIVE_SNAPSHOT_INTERVAL_MS = 2000;
const NATIVE_WEB_GAP_THRESHOLD = 3;
const SAME_INCIDENT_COOLDOWN_MS = 10000;
const DEAD_STATE_FAILED_TAPS = 3;
const POINTER_COMPLETION_TIMEOUT_MS = 1000;

let started = false;
let pointerDownCount = 0;
let webEvents = [];
let pendingPointers = new Map();
let mainThreadTimer = null;
let nativeSnapshotTimer = null;
let lastMainThreadTick = 0;
let previousNativeDownCount = null;
let previousWebDownCount = null;
let lastIncidentByType = {};
let onlineHandler = null;
let failedTapStreak = 0;
let deadStateActive = false;
let deadStateStartedAt = 0;

const getPlugin = () => {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.FLCTouchDiagnostics || null;
};

const safeStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
};

const clampText = (value, max = 120) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);

const describeElement = element => {
  if (!element || element.nodeType !== 1) return null;
  const tag = String(element.tagName || '').toLowerCase();
  const classes = Array.from(element.classList || []).slice(0, 4);
  const sensitiveText = tag === 'input' || tag === 'textarea' || element.isContentEditable;
  let style = null;
  try {
    const computed = window.getComputedStyle(element);
    style = {
      position: computed.position,
      zIndex: computed.zIndex,
      pointerEvents: computed.pointerEvents,
      opacity: computed.opacity,
      visibility: computed.visibility,
      display: computed.display,
    };
  } catch (_) {}

  return {
    tag,
    id: clampText(element.id, 80),
    classes,
    role: clampText(element.getAttribute?.('role'), 60),
    ariaLabel: clampText(element.getAttribute?.('aria-label'), 100),
    text: sensitiveText ? '' : clampText(element.textContent, 100),
    disabled: Boolean(element.disabled || element.getAttribute?.('aria-disabled') === 'true'),
    style,
  };
};

const capturePointStack = (x, y) => {
  try {
    return (document.elementsFromPoint?.(x, y) || [])
      .slice(0, 6)
      .map(describeElement)
      .filter(Boolean);
  } catch (_) {
    return [];
  }
};

const captureRuntimeState = (x = null, y = null) => {
  const visualViewport = window.visualViewport;
  return {
    path: `${window.location.pathname}${window.location.search || ''}`.slice(0, 300),
    visibility: document.visibilityState,
    documentHasFocus: typeof document.hasFocus === 'function' ? document.hasFocus() : null,
    fullscreenElement: Boolean(
      document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement
    ),
    activeElement: describeElement(document.activeElement),
    topElements: Number.isFinite(x) && Number.isFinite(y) ? capturePointStack(x, y) : [],
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      visualWidth: visualViewport?.width || null,
      visualHeight: visualViewport?.height || null,
      visualOffsetTop: visualViewport?.offsetTop || null,
    },
    videoOverlayPresent: Boolean(document.querySelector('[class*="videoOverlay"]')),
    dialogCount: document.querySelectorAll('[role="dialog"], .MuiDialog-root').length,
  };
};

const pushWebEvent = event => {
  webEvents.push(event);
  if (webEvents.length > MAX_WEB_EVENTS) webEvents = webEvents.slice(-MAX_WEB_EVENTS);
};

const readQueue = () => {
  const storage = safeStorage();
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(QUEUE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE_ITEMS) : [];
  } catch (_) {
    return [];
  }
};

const writeQueue = queue => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (!queue.length) storage.removeItem(QUEUE_KEY);
    else storage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_ITEMS)));
  } catch (_) {}
};


const nativeSnapshot = async () => {
  try {
    const plugin = getPlugin();
    if (!plugin || typeof plugin.snapshot !== 'function') return null;
    return await plugin.snapshot();
  } catch (_) {
    return null;
  }
};

const sendPayload = async payload => {
  const response = await fetch('/api/mobile-wrapper/diagnostics/tap', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
    keepalive: true,
  });
  if (!response.ok) throw new Error(`Tap diagnostic upload failed (${response.status})`);
};

const flushQueue = async () => {
  const queue = readQueue();
  if (!queue.length) return;
  const remaining = [];
  for (const item of queue) {
    try {
      await sendPayload(item);
    } catch (_) {
      remaining.push(item);
    }
  }
  writeQueue(remaining);
};

const makeIncidentId = () => `tap-android-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const getIdentityContext = () => {
  let familyId = '';
  let learnerId = '';
  try {
    const learner = store.getState()?.flinkPlay?.learner;
    familyId = clampText(learner?.familyId, 80);
    learnerId = clampText(learner?._id, 80);
  } catch (_) {}
  return {
    deviceId: clampText(safeStorage()?.getItem(DEVICE_KEY), 128),
    familyId,
    learnerId,
  };
};

const reportIncident = async (type, details = {}, options = {}) => {
  const now = Date.now();
  if (!options.force && now - Number(lastIncidentByType[type] || 0) < SAME_INCIDENT_COOLDOWN_MS) return;
  lastIncidentByType[type] = now;

  const native = await nativeSnapshot();
  const payload = {
    incidentId: makeIncidentId(),
    platform: 'android',
    source: 'native-wrapper',
    timestamp: new Date(now).toISOString(),
    incident: type,
    ...getIdentityContext(),
    details,
    state: captureRuntimeState(details?.x, details?.y),
    webEvents: webEvents.slice(-MAX_WEB_EVENTS),
    native,
  };

  const queue = readQueue();
  queue.push(payload);
  writeQueue(queue);

  try {
    await sendPayload(payload);
    writeQueue(readQueue().filter(item => item?.incidentId !== payload.incidentId));
  } catch (_) {
    // Keep it queued. It will be retried on the next online/start event.
  }
};

const pointerKey = event => String(Number.isFinite(event.pointerId) ? event.pointerId : 'mouse');

const clearPendingTimers = pending => {
  if (!pending) return;
  if (pending.completionTimer) window.clearTimeout(pending.completionTimer);
  if (pending.clickTimer) window.clearTimeout(pending.clickTimer);
  pending.completionTimer = null;
  pending.clickTimer = null;
};

const enterDeadState = pending => {
  if (deadStateActive) return;
  deadStateActive = true;
  deadStateStartedAt = Date.now();
  // Visual marker disabled; RED_CROSS remains in diagnostics/logs.
  pushWebEvent({
    type: 'diagnostic-indicator',
    at: deadStateStartedAt,
    indicator: 'RED_CROSS',
    state: 'dead',
    failedTapStreak,
  });
  reportIncident(
    'dead_state_enter',
    {
      screenIndicator: 'RED_CROSS',
      failedTapStreak,
      reason: 'three_pointerdowns_without_pointerup_or_click',
      x: Math.round(pending?.x || 0),
      y: Math.round(pending?.y || 0),
      target: pending?.targetDescription || null,
    },
    { force: true }
  );
};

const recordIncompletePointer = (pending, reason) => {
  if (!pending || pending.countedIncomplete) return;
  pending.countedIncomplete = true;
  failedTapStreak += 1;
  pushWebEvent({
    type: 'pointerdown-without-completion',
    at: Date.now(),
    pointerId: pending.key,
    x: Math.round(pending.x || 0),
    y: Math.round(pending.y || 0),
    reason,
    failedTapStreak,
    target: pending.targetDescription,
  });
  if (failedTapStreak >= DEAD_STATE_FAILED_TAPS) enterDeadState(pending);
};

const recoverDeadState = event => {
  const now = Date.now();
  const durationMs = deadStateStartedAt ? now - deadStateStartedAt : 0;
  const previousFailedTapStreak = failedTapStreak;
  deadStateActive = false;
  deadStateStartedAt = 0;
  failedTapStreak = 0;
  // Visual marker disabled; GREEN_CIRCLE remains in diagnostics/logs.
  pushWebEvent({
    type: 'diagnostic-indicator',
    at: now,
    indicator: 'GREEN_CIRCLE',
    state: 'recovered',
    durationMs,
  });
  reportIncident(
    'dead_state_recovered',
    {
      screenIndicator: 'GREEN_CIRCLE',
      durationMs,
      failedTapStreak: previousFailedTapStreak,
      x: Math.round(event?.clientX || 0),
      y: Math.round(event?.clientY || 0),
      target: describeElement(event?.target),
    },
    { force: true }
  );
};


const onPointerDown = event => {
  pointerDownCount += 1;
  const key = pointerKey(event);
  const previous = pendingPointers.get(key);
  if (previous) {
    clearPendingTimers(previous);
    if (!previous.upReceived) {
      recordIncompletePointer(previous, 'next_pointerdown_before_pointerup');
    }
  }

  const target = event.target;
  const pending = {
    key,
    downAt: Date.now(),
    x: event.clientX,
    y: event.clientY,
    target,
    targetDescription: describeElement(target),
    upReceived: false,
    countedIncomplete: false,
    completionTimer: null,
    clickTimer: null,
  };

  pending.completionTimer = window.setTimeout(() => {
    const current = pendingPointers.get(key);
    if (!current || current !== pending || current.upReceived) return;
    pendingPointers.delete(key);
    recordIncompletePointer(current, 'pointerup_timeout');
  }, POINTER_COMPLETION_TIMEOUT_MS);

  pendingPointers.set(key, pending);
  pushWebEvent({
    type: 'pointerdown',
    at: pending.downAt,
    pointerId: key,
    x: Math.round(event.clientX),
    y: Math.round(event.clientY),
    target: pending.targetDescription,
  });
};

const onPointerUp = event => {
  const key = pointerKey(event);
  const pending = pendingPointers.get(key);
  pushWebEvent({
    type: 'pointerup',
    at: Date.now(),
    pointerId: key,
    x: Math.round(event.clientX),
    y: Math.round(event.clientY),
    target: describeElement(event.target),
  });
  if (!pending) return;

  if (pending.completionTimer) window.clearTimeout(pending.completionTimer);
  pending.completionTimer = null;
  pending.upReceived = true;

  const moved = Math.hypot(event.clientX - pending.x, event.clientY - pending.y);
  if (moved > 14) {
    clearPendingTimers(pending);
    pendingPointers.delete(key);
    return;
  }

  pending.clickTimer = window.setTimeout(() => {
    const current = pendingPointers.get(key);
    if (!current) return;
    pendingPointers.delete(key);
    const target = current.target;
    if (!target?.isConnected) return;
    if (target.disabled || target.getAttribute?.('aria-disabled') === 'true') return;
    recordIncompletePointer(current, 'pointerup_without_click');
    reportIncident('pointerup_without_click', {
      delayMs: Date.now() - current.downAt,
      x: Math.round(current.x),
      y: Math.round(current.y),
      target: current.targetDescription,
      failedTapStreak,
      screenIndicator: deadStateActive ? 'RED_CROSS' : '',
    });
  }, NO_CLICK_MS);
};

const onPointerCancel = event => {
  const key = pointerKey(event);
  const pending = pendingPointers.get(key);
  clearPendingTimers(pending);
  pendingPointers.delete(key);
  pushWebEvent({ type: 'pointercancel', at: Date.now(), pointerId: key });
};

const findPendingForClick = event => {
  const directKey = pointerKey(event);
  if (pendingPointers.has(directKey)) return pendingPointers.get(directKey);
  let candidate = null;
  pendingPointers.forEach(item => {
    if (!candidate || item.downAt > candidate.downAt) candidate = item;
  });
  return candidate;
};

const onClick = event => {
  const pending = findPendingForClick(event);
  const now = Date.now();
  const delayMs = pending ? now - pending.downAt : null;
  pushWebEvent({
    type: 'click',
    at: now,
    delayMs,
    x: Math.round(event.clientX || 0),
    y: Math.round(event.clientY || 0),
    target: describeElement(event.target),
  });

  if (deadStateActive) recoverDeadState(event);
  else failedTapStreak = 0;

  if (!pending) return;
  clearPendingTimers(pending);
  pendingPointers.delete(pending.key);
  if (delayMs > CLICK_DELAY_MS) {
    reportIncident('delayed_click', {
      delayMs,
      x: Math.round(pending.x),
      y: Math.round(pending.y),
      target: pending.targetDescription,
    });
  }
};

const startMainThreadWatch = () => {
  lastMainThreadTick = performance.now();
  mainThreadTimer = window.setInterval(() => {
    const now = performance.now();
    const lagMs = now - lastMainThreadTick - MAIN_THREAD_INTERVAL_MS;
    lastMainThreadTick = now;
    if (document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus())) return;
    if (lagMs > MAIN_THREAD_STALL_MS) {
      pushWebEvent({ type: 'main-thread-stall', at: Date.now(), lagMs: Math.round(lagMs) });
      reportIncident('main_thread_stall', { lagMs: Math.round(lagMs) });
    }
  }, MAIN_THREAD_INTERVAL_MS);
};

const pollNativeTouchGap = async () => {
  if (document.hidden) {
    previousNativeDownCount = null;
    previousWebDownCount = null;
    return;
  }

  const snapshot = await nativeSnapshot();
  const nativeDownCount = Number(snapshot?.touchDownCount);
  if (!Number.isFinite(nativeDownCount)) return;

  if (previousNativeDownCount !== null && previousWebDownCount !== null) {
    const nativeDelta = nativeDownCount - previousNativeDownCount;
    const webDelta = pointerDownCount - previousWebDownCount;
    const gap = nativeDelta - webDelta;
    if (gap >= NATIVE_WEB_GAP_THRESHOLD) {
      reportIncident('native_web_touch_gap', { nativeDelta, webDelta, gap });
    }
  }

  previousNativeDownCount = nativeDownCount;
  previousWebDownCount = pointerDownCount;
};

const resetTimingBaseline = () => {
  lastMainThreadTick = performance.now();
  previousNativeDownCount = null;
  previousWebDownCount = null;
};

export const getAndroidTapDiagnosticSnapshot = () => {
  const now = Date.now();
  return {
    started,
    pointerDownCount,
    pendingPointerCount: pendingPointers.size,
    failedTapStreak,
    deadStateActive,
    deadStateStartedAt: deadStateStartedAt || null,
    deadStateDurationMs: deadStateActive && deadStateStartedAt ? now - deadStateStartedAt : 0,
    screenIndicator: deadStateActive ? 'RED_CROSS' : '',
    webEvents: webEvents.slice(-MAX_WEB_EVENTS),
    state:
      typeof window !== 'undefined' && typeof document !== 'undefined'
        ? captureRuntimeState()
        : null,
  };
};

export const startAndroidTapDiagnostics = () => {
  if (started || typeof window === 'undefined' || typeof document === 'undefined') return;
  if (getMobileAppPlatform() !== 'android') return;
  started = true;

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerCancel, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('visibilitychange', resetTimingBaseline);

  onlineHandler = () => flushQueue();
  window.addEventListener('online', onlineHandler);

  startMainThreadWatch();
  nativeSnapshotTimer = window.setInterval(pollNativeTouchGap, NATIVE_SNAPSHOT_INTERVAL_MS);
  pollNativeTouchGap();
  flushQueue();

  const plugin = getPlugin();
  reportIncident(
    'diagnostics_started',
    {
      pluginAvailable: Boolean(plugin),
      pluginSnapshotAvailable: typeof plugin?.snapshot === 'function',
      userAgent: clampText(window.navigator?.userAgent, 240),
    },
    { force: true }
  );
};

export const stopAndroidTapDiagnostics = () => {
  if (!started) return;
  started = false;

  document.removeEventListener('pointerdown', onPointerDown, true);
  document.removeEventListener('pointerup', onPointerUp, true);
  document.removeEventListener('pointercancel', onPointerCancel, true);
  document.removeEventListener('click', onClick, true);
  document.removeEventListener('visibilitychange', resetTimingBaseline);
  if (onlineHandler) window.removeEventListener('online', onlineHandler);

  if (mainThreadTimer) window.clearInterval(mainThreadTimer);
  if (nativeSnapshotTimer) window.clearInterval(nativeSnapshotTimer);
  pendingPointers.forEach(clearPendingTimers);

  mainThreadTimer = null;
  nativeSnapshotTimer = null;
  onlineHandler = null;
  pendingPointers = new Map();
  previousNativeDownCount = null;
  previousWebDownCount = null;
  pointerDownCount = 0;
  webEvents = [];
  lastIncidentByType = {};
  failedTapStreak = 0;
  deadStateActive = false;
  deadStateStartedAt = 0;
};
